// Market lifecycle, derived client-side from the timestamps + resolvePrice
// the backend ships on every market. Consumers re-derive the status at
// render time (with a ticking `now`) instead of reading a fetched status
// field, so display state stays now-accurate between refetches. `pending`
// is the unresolved state of a hidden market (ingested outside the public
// feed — nobody can predict on it, so it has no voting concept): the feed
// never lists one, only a market page opened by id can meet it, and the
// surfaces show it as voting closed.
export type MarketStatus = 'open' | 'voting_closed' | 'resolved' | 'pending';

// `active` covers everything before resolution — both `open` (voting in
// progress) and `voting_closed` (voting ended, waiting on resolution).
// `pending` is no filter: hidden markets are excluded from the feed.
export type StatusFilter = 'open' | 'voting_closed' | 'resolved' | 'active';

// A market is resolved the moment `resolvePrice` is written (0 counts as
// written — the check is != null, never truthiness) OR once `resolvesAt`
// has passed: backend resolvers lag (hours is normal), so the date decides
// while the price is still null. Both date boundaries are inclusive — now
// === boundary already flips the state.
export function deriveMarketStatus(
  m: {
    votingEndsAt: string;
    resolvesAt: string;
    resolvePrice: number | null;
    // Absent on a prediction's market: a prediction exists only on a public
    // market.
    hidden?: boolean;
  },
  // ms epoch — the caller's ticking clock (useNow in render): a default
  // reading Date.now() here would be a clock read the React Compiler
  // memoizes away with the status.
  now: number,
): MarketStatus {
  if (m.resolvePrice != null) return 'resolved';
  if (now >= new Date(m.resolvesAt).getTime()) return 'resolved';
  if (m.hidden === true) return 'pending';
  if (now >= new Date(m.votingEndsAt).getTime()) return 'voting_closed';
  return 'open';
}

// Builds the MongoDB-style `filter` fragment selecting the requested bucket
// server-side, using date predicates only ($gt/$lt on votingEndsAt /
// resolvesAt — the resolvePrice early-resolve case is not expressible in a
// date filter). Returns a braceless JSON fragment ready to be comma-joined
// into a larger filter string; null status → null (no filter).
export function statusFilterPredicate(
  status: StatusFilter | null,
  now: Date = new Date(),
): string | null {
  if (status === null) return null;
  const nowIso = now.toISOString();
  switch (status) {
    case 'open':
      return `"votingEndsAt":{"$gt":"${nowIso}"}`;
    case 'voting_closed':
      return `"votingEndsAt":{"$lt":"${nowIso}"},"resolvesAt":{"$gt":"${nowIso}"}`;
    case 'resolved':
      return `"resolvesAt":{"$lt":"${nowIso}"}`;
    case 'active':
      return `"resolvesAt":{"$gt":"${nowIso}"}`;
  }
}
